/* Lightweight i18n: Dutch source strings are the keys; EN comes from dict.js. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { api } from "./api";
import { invalidateCached } from "./sharedData";
import { useAuth } from "./auth";
import { EN, NL } from "./dict";
import { enableAutoTranslate, disableAutoTranslate } from "./autoTranslate";

export const LANGS = [
  { code: "nl", label: "Nederlands" },
  { code: "en", label: "English" },
];

const browserLang = () => ((navigator.language || "nl").toLowerCase().startsWith("nl") ? "nl" : "en");
let currentLang = localStorage.getItem("pm_lang") || browserLang();

export const getLang = () => currentLang;
export const locale = () => (currentLang === "en" ? "en-GB" : "nl-NL");

const interpolate = (s, vars) => (vars ? Object.keys(vars).reduce((acc, k) => acc.split(`{${k}}`).join(String(vars[k])), s) : s);

export function translate(key, vars) {
  if (key == null) return key;
  const out = currentLang === "en" ? (EN[key] ?? key) : (NL[key] ?? key);
  return interpolate(out, vars);
}

const Ctx = createContext({ lang: currentLang, t: translate, setLang: () => {} });

export function I18nProvider({ children }) {
  const { user } = useAuth();
  const [lang, setLangState] = useState(currentLang);

  useEffect(() => {
    if (user?.lang && (user.lang === "nl" || user.lang === "en") && user.lang !== currentLang) {
      currentLang = user.lang;
      localStorage.setItem("pm_lang", user.lang);
      setLangState(user.lang);
    }
  }, [user]);

  useEffect(() => {
    document.documentElement.lang = lang;
    if (lang === "nl") enableAutoTranslate(); else disableAutoTranslate();
  }, [lang]);

  const setLang = useCallback(async (l) => {
    currentLang = l;
    localStorage.setItem("pm_lang", l);
    setLangState(l);
    if (user) {
      await api.put("/me/lang", { lang: l }).catch(() => {});
      invalidateCached("/auth/me");
      api.get("/assistant/signals?refresh=true").catch(() => {});
    }
  }, [user]);

  const t = useCallback((key, vars) => translate(key, vars), [lang]); // eslint-disable-line react-hooks/exhaustive-deps
  const value = useMemo(() => ({ lang, t, setLang }), [lang, t, setLang]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export const useI18n = () => useContext(Ctx);
export const useT = () => useContext(Ctx).t;
